import { Box, Grid, GridItem, Skeleton, Spinner, Text } from '@chakra-ui/react';
import { AuthActionTypes, getUser } from '@devd-client/devd/auth';
import {
  PageHeader,
  PageToolbar,
  TeamMenu,
  MetaTags,
  MetricInfoItems,
  MetricDashboard,
  useAiAssistantStore,
  Loader,
  DateFilter,
  RepoDto,
} from '@devd-client/devd/components';
import { Select } from 'chakra-react-select';

import { FC, useEffect, useState } from 'react';
import { Chatbox } from '../components/Chatbox';
import { ticketTypeFilterOptions } from '../helpers/home.utils';
import { useTicketDashboard } from '../hooks/useTicketDashboard';
import type { IssueThresholdItem } from '../helpers/home.types';

const filterOptions = [
  { value: 'team', label: 'Team' },
  { value: 'project', label: 'Project' },
];

export const Ticket: FC = () => {
  const { isOpen } = useAiAssistantStore();

  const [inputWidth, setInputWidth] = useState('220px');

  const {
    ticketType,
    setTicketType,
    filterBy,
    setFilterBy,
    selected,
    selectedTeam,
    setSelectedTeam,
    appState,
    dispatch,
    repos,
    setRepos,
    repoList,
    repoLoading,
    isLargeThan2120,
    isLargerThan1820,
    QUERY_KEYS,
    data,
    isFetching,
    type,
    setType,
    clickedTab,
    setClickedTab,
    showForecast,
    setShowForecast,
  } = useTicketDashboard();

  useEffect(() => {
    if (repoList?.dto?.length) {
      const longestLabel = repoList.dto.reduce(
        (max: string, repo: RepoDto) =>
          repo.name.length > max.length ? repo.name : max,
        ''
      );

      const approximateWidth = longestLabel.length * 8;
      setInputWidth(`${Math.min(approximateWidth, 800)}px`);
    }
  }, [repoList]);

  return (
    <>
      <MetaTags title="Ticket | Dashboard | Devdynamics" />

      <Box display={'flex'} flexDirection={'column'} width={'100%'}>
        <Box>
          <PageHeader
            headingText={'Ticket'}
            mb={'10px'}
            subHeadingText="Work Item represents all type of tasks and story excluding sub tasks"
            tooltip="Track how tickets like Work Items and Bugs move through your workflow. This dashboard highlights resolution speed, backlog health, and process efficiency to help teams stay agile and deliver effectively."
            // helpDocLink={helpDocLink}
          />
          <PageToolbar
            leftComp={
              <>
                <Box mr={3}>
                  <Text
                    fontSize="sm"
                    fontFamily="heading"
                    fontWeight="semibold"
                    color="text.secondary"
                    lineHeight="17px"
                    mb={2}
                  >
                    Ticket Type
                  </Text>
                  <Select
                    placeholder="Select Ticket Type"
                    isMulti={true}
                    useBasicStyles
                    onChange={(e: any) => {
                      setTicketType(e);
                    }}
                    size="sm"
                    value={ticketType}
                    options={ticketTypeFilterOptions}
                    isClearable={false}
                  />
                </Box>

                <Box mr={3}>
                  <Text
                    fontSize="sm"
                    fontFamily="heading"
                    fontWeight="semibold"
                    color="text.secondary"
                    lineHeight="17px"
                    mb={2}
                  >
                    Filter By
                  </Text>
                  <Select
                    placeholder="Select"
                    useBasicStyles
                    onChange={(e: any) => {
                      setFilterBy(e);
                    }}
                    size="sm"
                    value={filterBy}
                    options={filterOptions}
                  />
                </Box>
                {filterBy.value === 'team' && (
                  <TeamMenu
                    selectedTeam={selectedTeam}
                    handleSelectedTeam={setSelectedTeam}
                    AuthActionTypes={AuthActionTypes}
                    getUser={getUser}
                    appState={appState}
                    dispatch={dispatch}
                  />
                )}

                {filterBy.value === 'project' && (
                  <Box mr={3} maxWidth="800px" width={inputWidth}>
                    <Text
                      fontSize="sm"
                      fontFamily="heading"
                      fontWeight="semibold"
                      color="text.secondary"
                      lineHeight="17px"
                      mb={2}
                    >
                      Project
                    </Text>
                    <Select
                      placeholder="Select Projects"
                      isMulti={true}
                      useBasicStyles
                      onChange={(e: any) => {
                        setRepos(e);
                      }}
                      size="sm"
                      value={repos}
                      options={repoList?.dto?.map((repo: RepoDto) => ({
                        value: repo.name,
                        label: repo.name,
                      }))}
                    />
                  </Box>
                )}
              </>
            }
            rightComp={
              <DateFilter
                getUser={getUser}
                appState={appState}
                dispatch={dispatch}
                AuthActionTypes={AuthActionTypes}
              />
            }
          />
        </Box>

        <Grid
          my={4}
          templateColumns={
            isLargeThan2120
              ? 'repeat(6, 1fr)'
              : isLargerThan1820
              ? 'repeat(5, 1fr)'
              : 'repeat(4, 1fr)'
          }
          gap={4}
        >
          {isFetching
            ? Array(isLargeThan2120 ? 6 : isLargerThan1820 ? 5 : 4)
                .fill(null)
                .map((_, index) => (
                  <Box
                    key={index}
                    position={'relative'}
                    minWidth="305px"
                    height="109px"
                  >
                    <Skeleton minWidth="305px" height="109px" key={index} />
                    <Spinner position={'absolute'} top={'40%'} left={'43%'} />
                  </Box>
                ))
            : data?.map((item: IssueThresholdItem, idx: number) => (
                <GridItem key={idx}>
                  <MetricInfoItems
                    item={item}
                    setType={setType}
                    clickedTab={clickedTab}
                    setClickedTab={setClickedTab}
                  />
                </GridItem>
              ))}
        </Grid>

        <MetricDashboard
          selected={selected}
          selectedTeam={filterBy.value === 'project' ? '' : selectedTeam.value}
          ticketType={ticketType?.length === 1 ? ticketType[0].value : 'all'}
          setType={setType}
          type={type}
          queryKey={QUERY_KEYS.allIssueThreshold}
          repos={
            filterBy.value === 'team'
              ? []
              : repos.map((el: { value: string; label: string }) => el.value)
          }
          showForecast={showForecast}
          setShowForecast={setShowForecast}
        />
      </Box>
      {isOpen && <Chatbox />}
      {repoLoading && <Loader />}
    </>
  );
};

export default Ticket;
